import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { Chip, ChipRow } from "../components/Chip";
import { ExerciseSheet, KIND_LABEL, moveWhere } from "../components/ExerciseSheet";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { QuestionSearch } from "../components/Question";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { verdict60 } from "../domain/exerciseMeta";
import { allMoves, libraryDoseLine, moveById, moveMatches, position } from "../domain/moves";
import type { Exercise, ExerciseKind } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import "./Train.css";

type Kind = "all" | ExerciseKind;
type Place = "chair" | "standing" | "bed";

const KINDS: { id: Kind; label: string }[] = [
  { id: "all", label: "All" },
  { id: "legs", label: "Legs" },
  { id: "balance", label: "Balance" },
  { id: "core", label: "Core" },
  { id: "upper", label: "Upper" },
  { id: "cardio", label: "Cardio" },
  { id: "mobility", label: "Mobility" },
  { id: "breath", label: "Breath" },
];

const PLACES: { id: Place; label: string }[] = [
  { id: "chair", label: "Chair" },
  { id: "standing", label: "Standing" },
  { id: "bed", label: "Bed" },
];

/** Standing takes the counter and the wall too: all three are on the feet. */
function atPlace(ex: Exercise, place: Place): boolean {
  const pos = position(ex);
  if (place === "standing") return pos === "standing" || pos === "support" || pos === "wall";
  return pos === place;
}

/**
 * Every move — the reference's 48 and the library's 58 (ADR-051) — filterable by kind and by
 * position at once, and searchable by its English, Hindi or yoga name. Each opens its sheet.
 */
export default function Library() {
  const S = useKaya((s) => s.S);
  const history = useHistory();
  const [kind, setKind] = useState<Kind>("all");
  const [place, setPlace] = useState<Place | null>(null);
  const [q, setQ] = useState("");
  const [shown, setShown] = useState<Exercise | null>(null);

  const every = allMoves();
  const hipSafe = S.profile.hipSafe;
  const matching = every
    .filter((e) => kind === "all" || e.t === kind)
    .filter((e) => !place || atPlace(e, place))
    .filter((e) => moveMatches(e, q));
  const hidden = hipSafe ? matching.filter((e) => e.hipfree).length : 0;
  const list = matching.filter((e) => !(e.hipfree && hipSafe)).sort((a, b) => a.n.localeCompare(b.n));
  const heading = kind === "all" ? "All moves" : (KINDS.find((k) => k.id === kind)?.label ?? "All moves");

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Exercise library" back={{ to: "/train", label: "Back to Train" }} />

            <div className="train-filters">
              <QuestionSearch label="Search moves" value={q} onChange={setQ} placeholder="sit, ताड़ासन, anulom" />
              <ChipRow ariaLabel="Filter by kind">
                {KINDS.map((k) => (
                  <Chip key={k.id} selected={kind === k.id} onClick={() => setKind(k.id)}>
                    {k.label}
                  </Chip>
                ))}
              </ChipRow>
              <ChipRow ariaLabel="Filter by position">
                {PLACES.map((p) => (
                  <Chip key={p.id} selected={place === p.id} onClick={() => setPlace(place === p.id ? null : p.id)}>
                    {p.label}
                  </Chip>
                ))}
              </ChipRow>
            </div>

            {hipSafe ? (
              <Notice
                primary={{ label: "Session settings", onClick: () => history.push("/you/session") }}
              >
                Hip-safe mode is on, so squats and lunges are hidden and hip-sensitive moves say so.
              </Notice>
            ) : null}

            <RowGroup
              heading={
                <>
                  {heading}
                  <span className="train-hsub">
                    {list.length} of {every.length}
                    {hidden ? ` · ${hidden} hidden by hip-safe` : ""}
                  </span>
                </>
              }
            >
              {list.length ? (
                list.map((e) => {
                  const easier = verdict60(e.id) === "easier" && e.easier ? moveById(e.easier) : undefined;
                  return (
                    <NavRow
                      key={e.id}
                      icon={I[e.t]}
                      title={
                        <span className="train-lib-title">
                          {e.n}
                          {/* "Butterfly (bound angle)" already has its brackets; the sheet names the pose */}
                          {e.yoga && !e.n.includes("(") ? ` (${e.yoga})` : ""}
                          <Tag tone="neutral">{KIND_LABEL[e.t]}</Tag>
                          {e.hip && hipSafe ? <span className="hip">watch hip</span> : null}
                        </span>
                      }
                      subtitle={
                        <>
                          {moveWhere(e)}
                          {easier ? (
                            <span className="train-lib-easier">
                              easier: <b>{easier.n}</b>
                            </span>
                          ) : null}
                        </>
                      }
                      onClick={() => setShown(e)}
                    />
                  );
                })
              ) : (
                <NavRow kind="value" title="No move matches" subtitle="Try another word, or clear a filter" />
              )}
            </RowGroup>

            <p className="train-fine">
              Each move opens its how-to, the common mistakes, why it helps, an easier and a harder version, and
              a video.
            </p>
          </div>
        </Screen>
        {shown ? (
          <ExerciseSheet
            ex={shown}
            hipSafe={hipSafe}
            dose={(ex) => libraryDoseLine(S, ex)}
            onClose={() => setShown(null)}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
}
